import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { 
  scheduleNutritionReminder, 
  listScheduledReminders, 
  cancelScheduledReminder 
} from '@/utils/notificationsManager';
import { Trash2 } from 'lucide-react';

const CreateNotification = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [notificationType, setNotificationType] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('12:00');
  const [scheduledReminders, setScheduledReminders] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  // Handle user search
  useEffect(() => {
    const searchUsers = async () => {
      if (userSearchTerm.length < 3) {
        setUserSearchResults([]);
        return;
      }

      setIsSearchingUsers(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, email, name, username')
          .or(`email.ilike.%${userSearchTerm}%,name.ilike.%${userSearchTerm}%,username.ilike.%${userSearchTerm}%`)
          .limit(5);

        if (error) throw error;
        setUserSearchResults(data || []);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsSearchingUsers(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      if (userSearchTerm.length >= 3) {
        searchUsers();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [userSearchTerm]);

  // Handle sending notification
  const handleSendNotification = async () => {
    if (!title || !message) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setIsLoading(true);
    try {
      let userId = notificationType === 'user' ? selectedUserId : null;
      
      if (notificationType === 'user' && !userId) {
        toast.error('Por favor selecciona un usuario');
        setIsLoading(false);
        return;
      }

      const { error } = await supabase.from('notifications').insert({
        title,
        message,
        link_url: linkUrl || null,
        type: notificationType === 'user' ? 'personal' : 'system',
        user_id: userId || '00000000-0000-0000-0000-000000000000', // System notification ID
      });

      if (error) throw error;

      toast.success('Notificación enviada correctamente');
      
      // Reset form
      setTitle('');
      setMessage('');
      setLinkUrl('');
      setNotificationType('all');
      setSelectedUserId(null);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Error al enviar la notificación');
    } finally {
      setIsLoading(false);
    }
  };

  // For scheduled reminders, we'll now just show a message that this feature is disabled
  const handleScheduleReminder = async () => {
    toast.info('Las notificaciones programadas están temporalmente desactivadas', {
      description: 'Por favor, utiliza notificaciones normales por ahora.'
    });
  };

  useEffect(() => {
    // We'll keep this effect but it won't retrieve any actual scheduled reminders
    const getScheduledReminders = async () => {
      try {
        const reminders = await listScheduledReminders();
        // Since we're returning an empty array above, this will just set an empty array
        setScheduledReminders(reminders);
      } catch (error) {
        console.error('Error fetching scheduled reminders:', error);
      }
    };

    getScheduledReminders();
  }, []);

  const handleCancelReminder = async (id: string) => {
    try {
      await cancelScheduledReminder(id);
      // Update the UI to remove the cancelled reminder
      setScheduledReminders(prev => prev.filter(r => r.id !== id));
      toast.success('Recordatorio cancelado');
    } catch (error) {
      console.error('Error cancelling reminder:', error);
      toast.error('Error al cancelar el recordatorio');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enviar Notificación</CardTitle>
        <CardDescription>
          Envía notificaciones a todos los usuarios o a un usuario específico
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="notificationType">Tipo de Notificación</Label>
            <Select 
              value={notificationType} 
              onValueChange={setNotificationType}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Destinatario</SelectLabel>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  <SelectItem value="user">Usuario específico</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          {notificationType === 'user' && (
            <div className="space-y-2">
              <Label htmlFor="userSearch">Buscar Usuario</Label>
              <div className="relative">
                <Input 
                  id="userSearch"
                  placeholder="Buscar por email, nombre o username"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                />
                
                {userSearchTerm.length >= 3 && userSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {userSearchResults.map(user => (
                      <div 
                        key={user.id}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setSelectedUserId(user.id);
                          setUserSearchTerm(user.email || user.username || user.name || user.id);
                          setUserSearchResults([]);
                        }}
                      >
                        <div className="font-medium">{user.name || user.username || 'Usuario sin nombre'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {isSearchingUsers && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin h-4 w-4 border-2 border-vegana-primary border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              
              {selectedUserId && (
                <div className="bg-green-50 p-2 rounded border border-green-200 text-green-800 text-sm">
                  Usuario seleccionado correctamente
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input 
              id="title"
              placeholder="Título de la notificación"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Mensaje</Label>
            <Textarea 
              id="message"
              placeholder="Texto de la notificación"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="linkUrl">URL (opcional)</Label>
            <Input 
              id="linkUrl"
              placeholder="URL de enlace (opcional)"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
          </div>
          
          <div className="flex gap-4 justify-end">
            <Button 
              type="button" 
              onClick={handleSendNotification}
              disabled={isLoading || !message || !title}
            >
              Enviar Notificación
            </Button>
          </div>
          
          {/* Scheduled reminders section - we'll keep this but with a note that it's temporarily disabled */}
          <div className="pt-6 border-t mt-6">
            <h3 className="text-lg font-medium mb-4">Programar Recordatorio Nutricional</h3>
            <p className="text-yellow-600 bg-yellow-50 p-3 rounded mb-4 text-sm">
              Las notificaciones programadas están temporalmente desactivadas. Por favor, utiliza notificaciones normales por ahora.
            </p>
            
            {/* We'll keep the UI but disable the functionality */}
            <div className="space-y-4 opacity-50">
              <div className="space-y-2">
                <Label htmlFor="scheduleTime">Hora</Label>
                <Input 
                  id="scheduleTime"
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  disabled
                />
              </div>
              
              <Button 
                type="button" 
                onClick={handleScheduleReminder}
                disabled={true}
                variant="outline"
              >
                Programar Recordatorio
              </Button>
            </div>
            
            {/* Scheduled reminders list - this will be empty but we'll keep the UI structure */}
            {scheduledReminders.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Recordatorios programados:</h4>
                <ul className="space-y-2">
                  {scheduledReminders.map(reminder => (
                    <li key={reminder.id} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                      <div>
                        <span className="font-medium">{reminder.time}</span>
                        <span className="text-gray-600 ml-2">{reminder.message}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelReminder(reminder.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreateNotification;
